import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import {
  AutoCalibrationRule,
  isCalibrationRuleEnabled,
  preComplileCalibrationRule,
} from 'utils/models/Calibration';
import {
  getConditionTypeDefinition,
  getPeriodTypeOptions,
  getPlaceholderFormatters,
} from 'utils/models/CalibrationRuleConditions';
import { RatingScale } from 'utils/models/Performance';
import { useCampaignFlow } from 'views/Administration/CampaignFlowContext';
import SwitchInput from 'views/Widgets/Inputs/SwitchInput';
import Table, { moveRow, TableColumn } from 'views/Widgets/Inputs/Table';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import AutoCalibrationRuleEditorModal from './AutoCalibrationRuleEditorModal';

interface Props {
  name: string;
  value?: AutoCalibrationRule[];
  onChange: (rules: AutoCalibrationRule[]) => void;
  disabled?: boolean;
}

const DEFAULT_RATING_SCALE: RatingScale = [1, 2, 3, 4, 5];

const AutoCalibrationRulesInput: FC<Props> = ({
  name,
  value,
  onChange,
  disabled = false,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const rules = useMemo(() => value ?? [], [value]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const contextRatingScale = useCampaignFlow()?.ratingScale;
  const ratingScale = useMemo(
    () =>
      contextRatingScale?.length ? contextRatingScale : DEFAULT_RATING_SCALE,
    [contextRatingScale]
  );

  const periodNames = useMemo(
    () =>
      Object.fromEntries(
        getPeriodTypeOptions(intl).map((option) => [option.id, option.name])
      ),
    [intl]
  );

  const openEditor = useCallback((index: number | null) => {
    setEditingIndex(index);
    setIsEditorOpen(true);
  }, []);

  const toggleEditor = useCallback(() => setIsEditorOpen((open) => !open), []);

  const onSaveRule = useCallback(
    (rule: AutoCalibrationRule) => {
      if (editingIndex === null) {
        onChange([...rules, rule]);
      } else {
        onChange(rules.map((r, i) => (i === editingIndex ? rule : r)));
      }
    },
    [editingIndex, onChange, rules]
  );

  const setEnabled = useCallback(
    (index: number, enabled: boolean) => {
      onChange(rules.map((r, i) => (i === index ? { ...r, enabled } : r)));
    },
    [onChange, rules]
  );

  const move = useCallback(
    (index: number, offset: number) => {
      const next = moveRow(rules, index, index + offset);
      if (next !== rules) {
        onChange(next);
      }
    },
    [onChange, rules]
  );

  const confirmDelete = useCallback(() => {
    if (deletingIndex !== null) {
      onChange(rules.filter((_r, i) => i !== deletingIndex));
    }
    setDeletingIndex(null);
  }, [deletingIndex, onChange, rules]);

  const formatters = useMemo(() => getPlaceholderFormatters(intl), [intl]);

  const columns = useMemo(
    (): TableColumn<AutoCalibrationRule>[] => [
      {
        key: 'flag',
        header: (
          <FormattedMessage
            id="app.views.administration.campaign_flow.auto_calibration_rules.columns.flag"
            defaultMessage="Flag"
          />
        ),
        render: (rule) => (
          <>
            <div>
              {
                preComplileCalibrationRule(rule, ratingScale, formatters)
                  .compiledTemplate
              }
            </div>
            <small className="text-muted">{rule.template}</small>
          </>
        ),
      },
      {
        key: 'condition',
        header: (
          <FormattedMessage
            id="app.views.administration.campaign_flow.auto_calibration_rules.columns.condition"
            defaultMessage="Condition"
          />
        ),
        render: (rule) => {
          const definition = getConditionTypeDefinition(
            intl,
            rule.condition?.type
          );
          return (
            <>
              <div>{definition?.label ?? rule.condition?.type}</div>
              <small className="text-muted">
                {definition?.summarize(rule.condition) ?? ''}
              </small>
            </>
          );
        },
      },
      {
        key: 'look_back',
        header: (
          <FormattedMessage
            id="app.views.administration.campaign_flow.auto_calibration_rules.columns.look_back"
            defaultMessage="Look-back"
          />
        ),
        render: (rule) => (
          <span className="badge bg-light text-dark">
            {`${rule.period.duration} ${
              periodNames[rule.period.type] ?? rule.period.type
            }`}
          </span>
        ),
      },
      {
        key: 'active',
        header: (
          <FormattedMessage
            id="app.views.administration.campaign_flow.auto_calibration_rules.columns.active"
            defaultMessage="Active"
          />
        ),
        render: (rule, index) => {
          const enabled = isCalibrationRuleEnabled(rule);
          return (
            <SwitchInput
              id={`${name}-enabled-${index}`}
              value={enabled}
              disabled={disabled}
              onChange={() => setEnabled(index, !enabled)}
            />
          );
        },
      },
      {
        key: 'actions',
        width: '3rem',
        className: 'text-end',
        render: (_rule, index) => (
          <UncontrolledDropdown>
            <DropdownToggle
              className="btn btn-sm btn-rounded-circle btn-white"
              role="button"
              disabled={disabled}
              aria-label={formatMessage({
                id: 'app.views.administration.campaign_flow.auto_calibration_rules.actions.aria_label',
                defaultMessage: 'Rule actions',
              })}
            >
              <i className="fe fe-more-horizontal" />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => openEditor(index)}>
                <FormattedMessage
                  id="app.views.administration.campaign_flow.auto_calibration_rules.actions.edit"
                  defaultMessage="Edit"
                />
              </DropdownItem>
              <DropdownItem
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <FormattedMessage
                  id="app.views.administration.campaign_flow.auto_calibration_rules.actions.move_up"
                  defaultMessage="Move up"
                />
              </DropdownItem>
              <DropdownItem
                disabled={index === rules.length - 1}
                onClick={() => move(index, 1)}
              >
                <FormattedMessage
                  id="app.views.administration.campaign_flow.auto_calibration_rules.actions.move_down"
                  defaultMessage="Move down"
                />
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                className="text-danger"
                onClick={() => setDeletingIndex(index)}
              >
                <FormattedMessage
                  id="app.views.administration.campaign_flow.auto_calibration_rules.actions.delete"
                  defaultMessage="Delete"
                />
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [
      disabled,
      formatMessage,
      formatters,
      intl,
      move,
      name,
      openEditor,
      periodNames,
      ratingScale,
      rules.length,
      setEnabled,
    ]
  );

  return (
    <div>
      <Table
        name={name}
        rows={rules}
        columns={columns}
        disabled={disabled}
        onChange={onChange}
        rowClassName={(rule) =>
          isCalibrationRuleEnabled(rule) ? '' : 'text-muted'
        }
        emptyState={
          <p className="text-muted">
            <FormattedMessage
              id="app.views.administration.campaign_flow.auto_calibration_rules.empty"
              defaultMessage="No automatic calibration flags. Nobody will be flagged for attention in the Performance Dashboard."
            />
          </p>
        }
      />
      <Button
        color="white"
        disabled={disabled}
        onClick={() => openEditor(null)}
        data-testid={`${name}-add-rule`}
      >
        <i
          className="fe fe-plus"
          style={{ position: 'relative', top: '2px' }}
        />{' '}
        <FormattedMessage
          id="app.views.administration.campaign_flow.auto_calibration_rules.add_rule"
          defaultMessage="Add rule"
        />
      </Button>
      <ConfirmationDialogModal
        isOpen={deletingIndex !== null}
        toggle={() => setDeletingIndex(null)}
        confirmCallback={confirmDelete}
        title={formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.delete.title',
          defaultMessage: 'Delete this rule?',
        })}
        description={formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.delete.description',
          defaultMessage:
            'People currently flagged by this rule will no longer be flagged once the cycle is saved.',
        })}
        confirmText={formatMessage({
          id: 'app.views.administration.campaign_flow.auto_calibration_rules.delete.confirm',
          defaultMessage: 'Delete rule',
        })}
      />
      <AutoCalibrationRuleEditorModal
        isOpen={isEditorOpen}
        toggle={toggleEditor}
        rule={editingIndex === null ? null : (rules[editingIndex] ?? null)}
        onSave={onSaveRule}
      />
    </div>
  );
};

export default React.memo(AutoCalibrationRulesInput);
